import { Radio, RadioGroup } from '@thanx/uikit/radio'
import {
  getReceiptConfig,
  GET_RECEIPT_CONFIG_SUCCESS,
  updateReceiptConfig,
  UPDATE_RECEIPT_CONFIG_SUCCESS,
} from 'actions/config'
import { alert } from 'actions/flash'
import Badges from 'components/Badges'
import ConfirmModal from 'components/ConfirmModal'
import HeaderCell from 'components/HeaderCell'
import Section from 'components/Section'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { ReceiptConfigApiFields } from 'models/Config'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import { selectReceiptConfig } from 'selectors/config'
import Item from './Item'

const t = buildTranslate('cms.settings.receipt_settings')

const ReceiptSettings: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useDispatch()

  const config = useSelector(selectReceiptConfig)

  const [rejectNonPayment, setRejectNonPayment] = useState<string>('false')

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      const response = await dispatch(getReceiptConfig())
      if (response.type === GET_RECEIPT_CONFIG_SUCCESS) {
        const receiptConfig: ReceiptConfigApiFields =
          response.payload.data.receipt_config
        if (receiptConfig) {
          setRejectNonPayment(String(receiptConfig.reject_non_payment ?? false))
        }
      } else {
        displayAlert('danger', 'fetch_error')
      }
      setIsLoading(false)
    }

    fetchData()
  }, [dispatch, displayAlert])

  const handlePublish = async () => {
    setIsSubmitting(true)

    const response = await dispatch(
      updateReceiptConfig({
        reject_non_payment: rejectNonPayment === 'true',
      })
    )

    if (response.type === UPDATE_RECEIPT_CONFIG_SUCCESS) {
      displayAlert('success', 'submit_success')
    } else {
      displayAlert('danger', 'submit_error')
    }

    setIsModalOpen(false)
    setIsSubmitting(false)
  }

  return (
    <>
      <CmsPage
        showTitle={showTitle}
        title={t('title')}
        description={t('description')}
        isLoading={isLoading}
      >
        <Section className="p-m mt-l">
          <table className="full-width campaign-table">
            <thead>
              <tr>
                <HeaderCell>{t('receipt_type')}</HeaderCell>
                <HeaderCell>{t('accepted')}</HeaderCell>
              </tr>
            </thead>
            <tbody>
              <Item title={t('cash')} value={config?.cash} />
              <Item title={t('gift_card')} value={config?.gift_card} />
              <Item title={t('online')} value={config?.online} />
              <Item title={t('third_party')} value={config?.third_party} />
              <Item title={t('store_credit')} value={config?.store_credit} />
            </tbody>
          </table>
        </Section>

        <Section className="p-m mt-l">
          <h4 className="mb-s">{t('proof_of_payment.title')}</h4>
          <p className="body-text-4 grey-70 mb-m">
            {t('proof_of_payment.description')}
          </p>
          <RadioGroup
            value={rejectNonPayment}
            onChange={value => setRejectNonPayment(value)}
          >
            <Radio value="true">
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                {t('proof_of_payment.required')}
                <Badges values={[t('recommended')]} />
              </span>
            </Radio>
            {rejectNonPayment === 'true' && (
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('proof_of_payment.required_explanation')}
              </p>
            )}
            <Radio value="false" className="mt-s">
              {t('proof_of_payment.optional')}
            </Radio>
            {rejectNonPayment === 'false' && (
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('proof_of_payment.optional_explanation')}
              </p>
            )}
          </RadioGroup>
        </Section>

        <PublishBanner
          buttonText={t('publish')}
          isLoading={isSubmitting}
          disabled={false}
          buttonType="button"
          onClick={() => setIsModalOpen(true)}
        />
      </CmsPage>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing')}
        confirmText={t('modal.publish')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => handlePublish()}
      />
    </>
  )
}

export default ReceiptSettings
